<template>
  <div
    id="map"
    ref="map"
    v-on:mouseenter="mouseover = true"
    v-on:mouseleave="mouseover = false"
  >
    <div class="mapboxgl-ctrl-bottom-right">
      <CategoricalLegend
        v-if="loaded && !nonScoreIndicators.includes(scoreIndicator) && tiles.length > 1"
        v-bind:colorLegendData="scoreLegendData"
        v-bind:geotype="geotype"
      />
      <Legend
        v-if="loaded && (scoreIndicator === 'hhmedinc_24') && tiles.length <= 1"
        v-bind:colorLegendData="colorLegendData"
        v-bind:format="format"
        v-bind:legendMarks="legendMarks"
      />
      <CategoricalLegend
        v-if="loaded && !nonScoreIndicators.includes(scoreIndicator) && tiles.length <= 1"
        v-bind:colorLegendData="scoreLegendData"
        v-bind:geotype="geotype"
      />

    </div>
    <div id="basemap" class="mapboxgl-ctrl-top-right">
      <div
        class="mapbox-ctrl mapboxgl-ctrl-group basemaps">
        <BaseMapSelect v-on:style-change="setMapStyle($event)"/>
      </div>
    </div>
    <div id="homeExtent" class="mapboxgl-ctrl-top-left">
      <div
        v-on:click="setSelectedtoRegion()"
        class="mapbox-ctrl mapboxgl-ctrl-group">
        <button class="mapboxgl-ctrl-icon" type="button" title="Reset to Region">
          <font-awesome-icon icon="home"/>
        </button>
      </div>
    </div>
    <div id="AutoZoom" class="mapboxgl-ctrl-top-left">
      <div
        class="mapbox-ctrl mapboxgl-ctrl-group basemaps">
        <AutoZoom
          v-on:autoZoom="autoZoom=$event"
        />
      </div>
    </div>
    <div ref="mapgl" v-on:mouseleave="setHover(null)"></div>
    <MapboxLayer
      name="communities"
      v-bind:geoJSON="communitiesGeoJSON"
      v-bind:layers="communitiesLayer"
      v-bind:interactivity=true
      v-bind:before-layer-name=beforeLayers
      v-on:featureClick="communityClick($event)"
      v-on:featureMouseMove="communityHover($event)"
      v-on:featureMouseLeave="setHover(null)"
    />
    <Loader v-if="!loaded"/>
  </div>
</template>

<script>
  import debounce from 'lodash.debounce'
  import mapboxgl from 'mapbox-gl'
  import * as d3 from 'd3'
  import MapboxLayer from './map/MapboxLayer'
  import ArcRestLayer from './map/ArcRestLayer'
  import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
  import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
  import cartocolor from 'cartocolor'
  import * as turf from 'turf'
  import { diff } from 'deep-diff'
  import Loader from './map/Loader'
  import Legend from './map/Legend'
  import BaseMapSelect from "./map/BaseMapSelect";
  import AutoZoom from "./map/AutoZoom";
  import { naturalBreaks } from './map/NaturalBreaks'
  import CategoricalLegend from "@/components/map/CategoricalLegend";
  import overlaySelect from "@/components/map/overlaySelect";

  export default {
  name: 'MainMap',
  components: {CategoricalLegend, BaseMapSelect, AutoZoom, Legend, Loader, MapboxLayer, ArcRestLayer, overlaySelect},
  props: [
    'geotype',
    'allGeojsonByType',
    'scoreIndicator',
    'nonScoreIndicators',
    'compositeScore',
    'tiles',
    'normalizationIndicator',
    'selectedId',
    'hoverId',
    'filteredCounty',
    'indFromName',
    'getIndMoeName',
    'groupNames',
    'groups',
    'selectedShortcut',
    'inInerMode',
    'namesFromGeotype',
    'geotypesForMap',
    'scoreColorLookup',
  ],
  data: function () {
    return {
      idsInViewport: new Set(),
      loaded: false,
      mouseover: false,
      autoZoom: true,
      equity_layer: true,
      overrideClassification: null,
    }
  },
  computed: {
    map: function () {
      mapboxgl.accessToken = 'pk.eyJ1Ijoic2VtY29nIiwiYSI6ImlvNEFUcmMifQ.VmDLZZG7l6syscFzkBs5eg'
      let style = 'mapbox://styles/semcog/cilnv2pcp005x9kly0if8ttqh'
      return new mapboxgl.Map({
        container: this.$refs.mapgl,
        style,
        center: [-83.2437186609522, 42.454430721108764],
        zoom: 7.684440451829983,
        minZoom: 6,
        scrollZoom: true,
        dragRotate: false,
        attributionControl: false,
      }).addControl(new mapboxgl.NavigationControl(), 'top-left')
        .addControl(new mapboxgl.FullscreenControl({ container: this.$refs.map }))
        .addControl(new MapboxGeocoder({
          accessToken: mapboxgl.accessToken,
          bbox: [ -85.11, 41.55, -81.46, 43.32 ],
          zoom: 10,
          collapsed: true,
          marker: true,
          flyTo: false,
          mapboxgl: mapboxgl,
          clearOnBlur: true,
          clearAndBlurOnEsc: true,
        }).on('result', (r) => {
          this.setSelectedFromGeocode(r.result.geometry.coordinates[0], r.result.geometry.coordinates[1])
        }), 'bottom-left')
    },
    lineWidth: function () {
      let width = 0;
      if (this.geotype === 'county' || this.geotype === 'mcd') {
        width = 2
      } else if (this.geotype === 'CensusTract') {
        width = 1
      } else {
        width = 0.8
      }
      return ['case', ['in', ['get', 'geoid'], ['literal', [this.hoverId, this.selectedId]]], 4, width]
    },
    communitiesLayer: function () {
      let filter = ['>', 'totalpop_24', 0]
      if (this.filteredCounty) {
        filter = ['all', filter, ['any', ['==', 'county', this.filteredCounty], ['==', 'county2', this.filteredCounty]]]
      }
      if (!this.scoreIndicator) {
        return []
      }
      let color = '#fff'
      let divide = ['/']
      let agg = ['+']
      if (this.breakPoints.length) {
        color = ['step']
        if (this.tiles.length > 1) {
          this.tiles.forEach((t) => agg.push(["get", "score_" + t]))
          divide.push(agg)
          divide.push(this.tiles.length)
          color.push(divide)
        } else {
          color.push(['get', this.scoreIndicator])
        }
        for (let i = 0; i < this.breakPoints.length; i += 1) {
          color.push(this.colorPalette[i], this.breakPoints[i])
        }
        color.push(this.colorPalette[this.breakPoints.length])
      }
      return [
        {
          type: 'fill',
          filter: filter,
          paint: {
            'fill-color': color,
            'fill-opacity': (this.equity_layer ? 0.8 : 0.01),
          },
        },
        {
          type: 'line',
          filter: filter,
          paint: {
            'line-width': this.lineWidth,
            'line-color': 'black',
            'line-blur': 2,
          },
        },
      ]
    },
    beforeLayers: function () {
      return ['communities_line_1', 'mi-county-labelpoints', 'place-village', 'mcd-pop-label-2uiaf9', 'local-dx1f7q']
    },
    communitiesGeoJSON: function () {
      return this.allGeojsonByType.get(this.geotype)
    },
    popupLookup: function () {
      if (!this.communitiesGeoJSON) {
        return new Map()
      }
      return new Map(this.communitiesGeoJSON.features.map(f =>
        [
          f.properties.geoid,
          [
            turf.pointOnFeature(f.geometry).geometry.coordinates,
            `<div>${f.properties['name']}</div><div>${this.tooltipFormat(f.properties)}</div>`,
          ],
        ],
      ))
    },
    popupData: function () {
      return this.popupLookup.get(this.hoverId || this.selectedId)
    },
    featuresLookup: function () {
      if (!this.communitiesGeoJSON) {
        return new Map()
      }
      return new Map(this.communitiesGeoJSON.features.map(f =>
        [
          f.properties.geoid,
          f.properties,
        ],
      ))
    },
    rawBreakPoints: function () {
      let raw_values = Array.from(this.idsInViewport.values())
        .map(id => this.featuresLookup.get(id))
        .filter(f => (
          f &&
          f[this.scoreIndicator]
        ))
      let values = raw_values
        .map((f) => {
          let value = 0
          let sum = 0
          if (this.tiles.length) {
            this.tiles.forEach(
              (t) => {
                sum += f["score_" + t]
                value = sum / this.tiles.length
                if (this.nonScoreIndicators.includes(t)) {
                  value = f[this.scoreIndicator]
                }
              },
            )
          }
          return value
        })
      let maxvalue = Math.max(0, ...values)
      let minvalue = Math.min(Infinity, ...values)
      let count = values.length || 0
      if (count === 0 || maxvalue === 0) {
        return []
      }

      let raw = []
      let classification = this.overrideClassification || this.selectedShortcut && this.selectedShortcut.classification;
      if (classification === 'divergingRatio') {
        raw = [
          1 - ((1 - minvalue) / 2),
          1,
          ((maxvalue - 1) / 3) + 1,
        ]
      } else if (classification === 'AreaNaturalBreaks') {
        raw = naturalBreaks(values, 4, raw_values.map(f => f.acres))
      } else if (classification === 'AreaLogNaturalBreaks') {
        raw = naturalBreaks(values.map(Math.log1p), 4, raw_values.map(f => f.acres)).map(Math.expm1)
      } else if (classification === 'NaturalBreaks') {
        raw = naturalBreaks(values, 4)
      } else if (classification === 'LogNaturalBreaks') {
        raw = naturalBreaks(values.map(Math.log1p), 4).map(Math.expm1)
      } else if (classification === 'ScoreBreaks') {
        raw = [1, 2, 3, 4]
        let tiles_len = this.tiles.length
        if (tiles_len > 1) {
          if (this.geotype === 'county') {
            raw = [1, 2, 3, 4] //TODO: use a different classification method
          } else {
            raw = naturalBreaks(values, 4)
          }
        }
      } else if (classification === 'Binary') {
        raw = [0.5]
      }else if (classification === 'AreaViewportPercentile') {
        let sorted = raw_values.sort((a, b) =>
          (a[this.scoreIndicator]) -
          (b[this.scoreIndicator])
        )
        let cumsumAcres = sorted.map((sum => value => sum += value.acres)(0))
        let totalAcres = cumsumAcres[cumsumAcres.length - 1]
        let percentiles = [0.05, 0.30, 0.70, 0.95]
        raw = percentiles.map(v => {
          let i = cumsumAcres.findIndex(s => s >= (v * totalAcres)) // TODO O(n^2)
          return sorted[i][this.scoreIndicator]
        })
      } else {
        // defeat to viewportPercentile if custom map

        let sorted = values.sort((a, b) => a - b)
        let percentiles = [0.05, 0.30, 0.70, 0.95]
        raw = percentiles.map(v => sorted[Math.floor(v * count)])
      }
      return raw
    },
    breakPoints: function () {
      let out = []
      let max = -Infinity
      this.rawBreakPoints.forEach(b => {
        if (b > max) {
          out.push(b)
          max = b
        }
      })
      return out
    },
    colorPalette: function () {
      if (!this.selectedShortcut){
        return cartocolor['Emrld'][this.breakPoints.length + 1] || []
      }
      if (cartocolor[this.selectedShortcut.cartoColor]) {
        return cartocolor[this.selectedShortcut.cartoColor][this.breakPoints.length + 1] || []
      }
      let out = this.selectedShortcut.cartoColor.split(',') || [];
      while (out.length <= this.breakPoints.length) {
        out.push('#000')
      }
      return out
    },
    colorLegendData: function () {
      let out = []
      for (let i = 0; i <= this.breakPoints.length; i += 1) {
        out.push(
          {
            key: [
              this.breakPoints[i - 1] || -Infinity,
              this.breakPoints[i] || Infinity,
            ],
            value: this.colorPalette[i],
          }
        )
      }
      return out
    },
    scoreLegendData: function (){
      return [
          {//#fffad7,#fce7a8,#f5c25e,#78c679,#006837
            key: 0,
            value: '#fffad7',
            visibility: true,
            label: this.tiles.length > 1 ? 'Low' : '0 - Low',
          },
          {
            key: 1,
            value: '#fce7a8',
            visibility: true,
            label: this.tiles.length > 1 ? '' : '1',
          },
          {
            key: 2,
            value: '#f5c25e',
            visibility: true,
            label: this.tiles.length > 1 ? '' : '2',
          },
          {
            key: 3,
            value: '#78c679',
            visibility: true,
            label: this.tiles.length > 1 ? '' : '3',
          },
          {
            key: 4,
            value: '#006837',
            visibility: true,
            label: this.tiles.length > 1 ? 'High' : '4 - High',
          }]
    },
    binaryLegendData: function (){
      return [
          {//#f6eff7,#bdc9e1,#67a9cf,#1c9099,#016c59
            key: 0,
            value: '#f6edbd',
            visibility: true,
            label: 'Not Disadvantaged',
          },
          {
            key: 1,
            value: '#ca562c',
            visibility: true,
            label: 'Disadvantaged',
          },
]
    },
    dac_cvrLegendData: function (){
      return [
          {//#f6eff7,#bdc9e1,#67a9cf,#1c9099,#016c59
            key: 0,
            value: '#f6edbd',
            visibility: true,
            label: 'Not Disadvantaged',
          },
          {
            key: 1,
            value: '#fc921f',
            visibility: true,
            label: 'Disadvantaged',
          },
]
    },
    raiseLegendData: function (){
      return [
          {//#f6eff7,#bdc9e1,#67a9cf,#1c9099,#016c59
            key: 0,
            value: '#f6edbd',
            visibility: true,
            label: 'Not APP',
          },
          {
            key: 1,
            value: '#3d5941',
            visibility: true,
            label: 'APP',
          },
]
    },
    popup: function () {
      return new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      })
    },
    format: function () {
      let format = d3.format((this.selectedShortcut && this.selectedShortcut.D3FormatterString) || '.2f');
      if (this.selectedShortcut.classification === 'ScoreBreaks') {
        format = d3.format('.2f')
      }
      return format
    },
    maxMapValue: function () {
      if (!this.communitiesGeoJSON) {
        return 0
      }
      return Math.max(0, ...this.communitiesGeoJSON.features.map(f => f.properties)
        .filter(f => (
          f[this.scoreIndicator]
        ))
        .map((f) => {
          let value = 0
          let sum = 0
          if (this.tiles.length) {
            this.tiles.forEach(
              (t) => {
                sum += f["score_" + t]
                value = sum / this.tiles.length
                if (this.nonScoreIndicators.includes(t)) {
                  value = f[this.scoreIndicator]
                }
              },
            )
          }
          return value
        }))
    },
    legendMarks: function () {
      let d = [0].concat(this.colorLegendData.map(legend => legend.key[1]).filter(isFinite)).concat([this.maxMapValue])
      return Array.from(new Set(d))
    },
  },
  methods: {
    setHover: debounce(function (id) {
      if (this.mouseover || !id) {
        if (this.hoverId !== id) {
          this.$emit('set-hov-geom-id', id)
        }
      }
    }, 100),
    setSelectedtoRegion: function () {
      this.setSelected(8999)
      this.zoomTo(8999)
    },
    setSelected: function (id) {
      this.$emit('set-sel-geom-id', id)
    },
    communityClick: function (featureEvent) {
      if (featureEvent.features.length > 0) {
        const feature = featureEvent.features[0]
        this.$emit('set-sel-geom-id', feature.properties.geoid)
      }
    },
    communityHover: function (featureEvent) {
      if (featureEvent.features.length > 0) {
        const feature = featureEvent.features[0]
        this.setHover(feature.properties.geoid)
      } else {
        this.setHover(null)
      }
    },
    setMapStyle: function (e) {
      this.map.setStyle(e)
    },
    setStyle: debounce(function () {
      if (!this.map.getLayer('communities_fill_0')) {
        return
      }
      let alldata = new Set();
      if (this.communitiesGeoJSON){
        alldata = new Set(this.communitiesGeoJSON.features.map(f => f.properties.geoid))
      }
        this.idsInViewport = Object.freeze(alldata)
        this.$nextTick(() => {
          this.loaded = true
        })
    }, 150, { leading: true, maxWait: 600 }),
    zoomTo: function (id) {
      if (!this.communitiesGeoJSON || !this.autoZoom) {
        return
      }
      let extent = {
        93: [[-84.2579218629181, 42.3239034763136], [-83.564870927822, 42.8832526817047]],
        99: [[-83.202747340568, 42.3471894195991], [-82.6063045158249, 42.9974602667658]],
        115: [[-83.87391435586, 41.6235951031165], [-83.0785807251129, 42.1942805362744]],
        125: [[-83.7894115599797, 42.3312327335914], [-82.9834924191637, 42.9886278835273]],
        147: [[-83.0960949946948, 42.418557292642], [-82.3184528982991, 43.2688321383929]],
        161: [[-84.2340738132845, 41.9715949343173], [-83.4394579054165, 42.5351067992344]],
        163: [[-83.6516108768434, 41.9331541851303], [-82.7701109750752, 42.5510028516089]],
      }[id]
      if (!extent) {
        let geo = this.communitiesGeoJSON.features.find(geo => geo.properties.geoid === id)
        if (geo) {
          let bbox = turf.bbox(turf.buffer(
            turf.bboxPolygon(turf.bbox(geo)),
            17 + 0.0000004 * Math.sqrt(geo.properties.acres || 0), //TODO: tune to match existing behaviour
            { units: 'kilometers', steps: 4 },
          ))
          extent = [[bbox[0], bbox[1]], [bbox[2], bbox[3]]]
        }
      }
      if (!extent) {
        extent = [[-84.31148767827142, 41.69721968975264], [-82.27975135795727, 43.1754453109819]]
      }
      this.map.fitBounds(
        extent,
        { padding: 1 },
      )
    },
    selectedCompositeColor: function (compositeScore) {
      let color = 'grey'
      this.breakPoints.forEach(
        (b, index) => {
          if (compositeScore >= b) {
            color = this.colorPalette[index + 1]
          }
        },
      )
      return color
    },
    setSelectedFromGeocode: function (x, y) {
      if (!this.map.getLayer('communities_fill_0')) {
        return
      }
      let features = this.map.queryRenderedFeatures(this.map.project([x, y]), { layers: ['communities_fill_0'] });
      if (features.length) {
        this.setSelected(features[0].properties.geoid)
      }
    },
    tooltipFormat: function (properties) {
      let score = 0
      let text = ''
      this.tiles.forEach(
        (t) => {
          if (this.scoreColorLookup[score] && this.tiles.length === 1 && !this.nonScoreIndicators.includes(t) && this.equity_layer){
            score += properties["score_" + t]
            text = '<strong>Score: ' + score + '</strong>'
          }
          if (this.nonScoreIndicators.includes(t)) {
            score = properties[this.scoreIndicator]
            if (this.scoreIndicator === 'disadvantagedcommunity') {
              text = '<strong>' + (score === 1 ? "Disadvantaged" : "Not Disadvantaged") + '</strong>'
            } else if (this.scoreIndicator === 'raise') {
              text = '<strong>' + (score === 1 ? "APP" : "Not APP") + '</strong>'
            } else if (this.scoreIndicator === 'dac_cvr') {
              text = '<strong>' + (score === 1 ? "Disadvantaged" : "Not Disadvantaged") + '</strong>'
            } else {
              text = '<strong>' + this.format(score) + '</strong>'
            }
          }
        },
      )
      return text
    },
  },
  mounted: function () {
    this.$once('hook:beforeDestroy', function () {
      this.map.remove()
    })
    this.map.once('idle', () => {
      this.setStyle();
      this.zoomTo(this.selectedId || this.filteredCounty)
      this.map.on('sourcedata', (e) => {
        if (e.sourceId === 'communities') {
          this.setStyle();
          if (this.tiles.length > 1) {
            this.$emit('composite-color', this.selectedCompositeColor(this.compositeScore))
          }
        }
      });
      this.map.once('styleimagemissing', () => {
        this.map.loadImage('images/dashed_hash.png', (err, image) => {
          this.map.addImage('pattern', image);
        })
      })
    });
  },
  watch: {
    selectedId: function () {
      this.zoomTo(this.selectedId || this.filteredCounty)
    },
    popupData: function () {
      let data = this.popupData
      if (data) {
        this.popup.setLngLat(data[0])
        this.popup.setHTML(data[1])
        this.popup.addTo(this.map)
      } else {
        this.popup.remove()
      }
    },
    filteredCounty: function () {
      this.zoomTo(this.filteredCounty)
    },
    compositeScore: {
      immediate: false,
      handler: function () {
        this.$emit('composite-color', this.selectedCompositeColor(this.compositeScore))
      },
    },
    colorLegendData: function (oldColorLegendData, newColorLegendData) {
      let changes = diff(oldColorLegendData, newColorLegendData)
      if (changes) {
        this.$emit('set-legend-color-data', this.colorLegendData)
      }
    },
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only. /deep/ applies to children as well -->
<style scoped>
  #map {
    grid-column: 1;
    grid-row: 2;
    position: relative;
  }

  /deep/ .mapboxgl-map {
    width: 100%;
    height: 100%;
  }

  /deep/ .mapboxgl-ctrl-top-right {
    max-width: calc(100% - 50px);
    z-index: 1 !important;
  }

  .mapboxgl-ctrl-top-right > div.basemaps {
    background-color: white;
    opacity: 0.9;
    padding: 0;
    margin: 50px 10px 0 0;
    pointer-events: auto;
    border-radius: 2px;
  }

  /deep/ .mapboxgl-ctrl-top-left {
    max-width: calc(100% - 50px);
    z-index: 1 !important;
  }

  /deep/ .mapboxgl-ctrl-bottom-left {
    z-index: 1 !important;
  }

  .mapboxgl-ctrl-top-left>div.name {
    background-color: white;
    opacity: 0.9;
    padding: 7px;
    margin: 10px 0 0 50px;
    pointer-events: auto;
    border-radius: 15px;
  }

  .mapboxgl-ctrl-bottom-right {
    max-width: calc(100% - 250px);
    z-index: 1;
  }

  #homeExtent {
    margin: 110px 0px 10px 10px;
    pointer-events: auto;
    position: absolute
  }

      #AutoZoom {
    pointer-events: auto;
    position: absolute;
    margin: 150px 0 10px 10px;
    grid-row: 2;
  }

  /deep/ .mapboxgl-popup {
    display: block !important;
    z-index: 1;
    pointer-events: none !important;
  }

  /deep/ .mapboxgl-popup strong {
    font-size: medium;
  }

  /deep/ .mapboxgl-popup small {
    color: #9aa5a9;
    font-size: small;
  }

  /deep/ .mapboxgl-popup .mapboxgl-popup-content {
    background: #292424;
    color: white;
    opacity: 0.95;
    border-radius: 16px;
    padding: 5px 10px 4px;
    pointer-events: none !important;
  }

  /deep/ .mapboxgl-popup .mapboxgl-popup-tip {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: #292424;
    opacity: 0.8;
    z-index: 1;
  }

  /deep/ .mapboxgl-popup {
    margin-bottom: 10px;
  }

  /deep/ .mapboxgl-popup .mapboxgl-popup-tip {
    border-width: 5px 5px 0 5px;
    border-left-color: transparent !important;
    border-right-color: transparent !important;
    border-bottom-color: transparent !important;
    bottom: -5px;
    left: calc(50% - 10px);
    margin-top: 0;
    margin-bottom: 0;
  }

  @media (max-width: 1200px) {
    .mapboxgl-ctrl-bottom-right {
      max-width: calc(100% - 50px);
    }
  }

  @media (max-width: 576px), print {
    #map {
      height: 75vh;
      grid-row: 2;
    }

    .mapboxgl-ctrl-bottom-right {
      max-width: calc(100% - 180px);
    }
  }
</style>
